import React from "react";
import { participantApi } from "../../store/participant/participant";
import { PageSkeleton } from "../common/PageSkeleton";
import {
  Box,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { IoAnalyticsSharp } from "react-icons/io5";
import { TbMessages } from "react-icons/tb";
import { useLingui } from "@lingui/react";
import { Badge } from "../preview/Badge";
import { PresentationSummary as PresentationSummaryType } from "../../types/presentation.types";

interface FeatureProps {
  text: string;
  message: string | React.ReactElement;
  iconBg: string;
  icon?: React.ReactElement;
}

const Feature = ({ text, icon, iconBg, message }: FeatureProps) => {
  return (
    <Stack
      direction={{ base: "column", md: "row" }}
      align={{ base: "start", md: "center" }}
      w={"100%"}
    >
      <Flex
        w={8}
        h={8}
        align={"center"}
        justify={"center"}
        rounded={"full"}
        bg={iconBg}
      >
        {icon}
      </Flex>
      <Text fontWeight={600}>{text}</Text>
      {typeof message === "string" ? <Text>{message}</Text> : message}
    </Stack>
  );
};

interface PresentationSummaryProps {
  participantId: number;
  presentations?: PresentationSummaryType[] | null;
  originalPresentations?: PresentationSummaryType[] | null;
}

export const PresentationSummary: React.FC<PresentationSummaryProps> = ({
  participantId,
  presentations,
  originalPresentations,
}) => {
  const { i18n } = useLingui();
  const [
    fetchPresentations,
    { data: presentationFetch = [], isLoading: arePresentationsLoading },
  ] = participantApi.endpoints.listParticipantPresentation.useLazyQuery();

  React.useEffect(() => {
    if (!presentations) {
      fetchPresentations(participantId);
    }
  }, [presentations, participantId, fetchPresentations]);

  const presentationList = React.useMemo(
    () => (presentations ? presentations : presentationFetch),
    [presentations, presentationFetch]
  );

  const hasChanges = originalPresentations !== undefined;

  // Match current and original rows primarily by stable ID to avoid false "new"
  // state when editable fields (e.g. title/topic) change during preview.
  const findOriginalPresentation = (
    presentation: PresentationSummaryType,
    index?: number
  ) => {
    if (!originalPresentations) return null;

    if (presentation.id > 0) {
      const byId = originalPresentations.find((orig) => orig.id === presentation.id);
      if (byId) return byId;
    }

    const byContent = originalPresentations.find(
      (orig) =>
        orig.presentationTitle === presentation.presentationTitle &&
        orig.performers === presentation.performers &&
        orig.performersWorkplace === presentation.performersWorkplace
    );
    if (byContent) return byContent;

    if (index !== undefined && index >= 0 && index < originalPresentations.length) {
      return originalPresentations[index];
    }

    return null;
  };

  // Helper function to check if a field changed
  const fieldChanged = (
    field: keyof PresentationSummaryType,
    current: PresentationSummaryType,
    original: PresentationSummaryType | null | undefined
  ) => {
    if (!hasChanges || !original) return false;
    return current[field] !== original[field];
  };

  const hasAnyChanges = React.useMemo(() => {
    if (!hasChanges || !originalPresentations) return false;
    if (presentationList.length !== originalPresentations.length) return true;
    return presentationList.some((pres, index) => {
      const orig = findOriginalPresentation(pres, index);
      if (!orig) return true; // New presentation
      return (
        pres.presentationTitle !== orig.presentationTitle ||
        pres.performers !== orig.performers ||
        pres.performersWorkplace !== orig.performersWorkplace ||
        pres.presentationType !== orig.presentationType ||
        pres.presentationTopic !== orig.presentationTopic
      );
    });
  }, [
    hasChanges,
    originalPresentations,
    presentationList,
    findOriginalPresentation,
  ]);

  return arePresentationsLoading ? (
    <PageSkeleton />
  ) : (
    <Box
      borderRadius={hasAnyChanges ? "lg" : "none"}
      _before={
        hasAnyChanges
          ? {
              content: '""',
              position: "absolute",
              inset: "0px",
              zIndex: -1,
              borderRadius: "lg",
              filter: "blur(4px)",
              pointerEvents: "none",
              boxShadow: `
                -3px -3px 8px rgba(245, 101, 101, 0.15),
                1.5px -2px 9px rgba(237, 100, 166, 0.15),
                -4.5px  1.5px 10px rgba(237, 137,  54, 0.15),
                1.5px  3px 8px rgba(237, 100, 166, 0.125),
                -3px 5.5px 8px rgba(245, 101, 101, 0.125),
                3px -3px 9px rgba(237, 137,  54, 0.125)
              `,
            }
          : undefined
      }
      zIndex={0}
      position="relative"
    >
      <Stack>
        {presentationList.map((presentation, index) => {
          const originalPresentation = findOriginalPresentation(presentation, index);
          const isNewPresentation = hasChanges && !originalPresentation;
          return (
            <Stack key={index}>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={10}
                key={index}
                mb={8}
                minChildWidth="70%"
              >
                <Stack spacing={4}>
                  <Badge color="blue">
                    <Trans>Presentations</Trans>
                  </Badge>
                  {(() => {
                    const titleChanged = fieldChanged(
                      "presentationTitle",
                      presentation,
                      originalPresentation
                    );
                    return (
                      <Heading as="h3" size="lg">
                        {titleChanged || isNewPresentation ? (
                          <>
                            <Text as="span" color="green.500" fontWeight={600}>
                              {presentation.presentationTitle}
                            </Text>
                            {originalPresentation ? (
                              <Text
                                as="span"
                                color="red.500"
                                ml={2}
                                textDecoration="line-through"
                              >
                                ({originalPresentation.presentationTitle})
                              </Text>
                            ) : (
                              <Text
                                as="span"
                                color="red.500"
                                ml={2}
                                textDecoration="line-through"
                              >
                                (-)
                              </Text>
                            )}
                          </>
                        ) : (
                          presentation.presentationTitle
                        )}
                      </Heading>
                    );
                  })()}
                  <Stack>
                    <HStack>
                      <Text color="gray.500">
                        <Trans>Name of author(s):</Trans>
                      </Text>
                      <Text>
                        {(() => {
                          const performersChanged = fieldChanged(
                            "performers",
                            presentation,
                            originalPresentation
                          );
                          if (performersChanged || isNewPresentation) {
                            return (
                              <>
                                <Text
                                  as="span"
                                  color="green.500"
                                  fontWeight={600}
                                >
                                  {presentation.performers}
                                </Text>
                                {originalPresentation ? (
                                  <Text
                                    as="span"
                                    color="red.500"
                                    ml={2}
                                    textDecoration="line-through"
                                  >
                                    ({originalPresentation.performers})
                                  </Text>
                                ) : (
                                  <Text
                                    as="span"
                                    color="red.500"
                                    ml={2}
                                    textDecoration="line-through"
                                  >
                                    (-)
                                  </Text>
                                )}
                              </>
                            );
                          }
                          return presentation.performers;
                        })()}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text color="gray.500">
                        <Trans>Workplace of author(s):</Trans>
                      </Text>
                      <Text>
                        {(() => {
                          const workplaceChanged = fieldChanged(
                            "performersWorkplace",
                            presentation,
                            originalPresentation
                          );
                          if (workplaceChanged || isNewPresentation) {
                            return (
                              <>
                                <Text
                                  as="span"
                                  color="green.500"
                                  fontWeight={600}
                                >
                                  {presentation.performersWorkplace}
                                </Text>
                                {originalPresentation ? (
                                  <Text
                                    as="span"
                                    color="red.500"
                                    ml={2}
                                    textDecoration="line-through"
                                  >
                                    ({originalPresentation.performersWorkplace})
                                  </Text>
                                ) : (
                                  <Text
                                    as="span"
                                    color="red.500"
                                    ml={2}
                                    textDecoration="line-through"
                                  >
                                    (-)
                                  </Text>
                                )}
                              </>
                            );
                          }
                          return presentation.performersWorkplace;
                        })()}
                      </Text>
                    </HStack>
                  </Stack>
                  <Stack spacing={4}>
                    <Feature
                      icon={
                        <Icon
                          as={IoAnalyticsSharp}
                          color={"yellow.500"}
                          w={5}
                          h={5}
                        />
                      }
                      iconBg={"yellow.100"}
                      text={i18n._("Presentation form:")}
                      message={(() => {
                        const typeChanged = fieldChanged(
                          "presentationType",
                          presentation,
                          originalPresentation
                        );
                        if (typeChanged || isNewPresentation) {
                          return (
                            <>
                              <Text
                                as="span"
                                color="green.500"
                                fontWeight={600}
                              >
                                {presentation.presentationType}
                              </Text>
                              {originalPresentation ? (
                                <Text
                                  as="span"
                                  color="red.500"
                                  ml={2}
                                  textDecoration="line-through"
                                >
                                  ({originalPresentation.presentationType})
                                </Text>
                              ) : (
                                <Text
                                  as="span"
                                  color="red.500"
                                  ml={2}
                                  textDecoration="line-through"
                                >
                                  (-)
                                </Text>
                              )}
                            </>
                          );
                        }
                        return presentation.presentationType;
                      })()}
                    />
                    <Feature
                      icon={
                        <Icon as={TbMessages} color={"green.500"} w={5} h={5} />
                      }
                      iconBg={"green.100"}
                      text={i18n._("Topic of presentation:")}
                      message={(() => {
                        const topicChanged = fieldChanged(
                          "presentationTopic",
                          presentation,
                          originalPresentation
                        );
                        if (topicChanged || isNewPresentation) {
                          return (
                            <>
                              <Text
                                as="span"
                                color="green.500"
                                fontWeight={600}
                              >
                                {presentation.presentationTopic}
                              </Text>
                              {originalPresentation ? (
                                <Text
                                  as="span"
                                  color="red.500"
                                  ml={2}
                                  textDecoration="line-through"
                                >
                                  ({originalPresentation.presentationTopic})
                                </Text>
                              ) : (
                                <Text
                                  as="span"
                                  color="red.500"
                                  ml={2}
                                  textDecoration="line-through"
                                >
                                  (-)
                                </Text>
                              )}
                            </>
                          );
                        }
                        return presentation.presentationTopic;
                      })()}
                    />
                  </Stack>
                </Stack>
              </SimpleGrid>
              {presentationList.length > 1 &&
                presentationList.length - 1 !== index && <Divider mb={8} />}
            </Stack>
          );
        })}
        {presentationList.length === 0 && (
          <Stack>
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              mb={8}
              minChildWidth="70%"
            >
              <Stack spacing={4}>
                <Badge color="blue">
                  <Trans>Presentation</Trans>
                </Badge>
                <Text>
                  <Trans>I don't announce presentation(s)</Trans>
                </Text>
              </Stack>
            </SimpleGrid>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
